/**
 * Tabs Block - Interactivity API View Script
 *
 * Handles tab switching, keyboard navigation, and ARIA updates.
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

/**
 * Update ARIA attributes and panel visibility.
 */
const updateTabsState = ( container, activeIndex, shouldFocus ) => {
	const tabs = container.querySelectorAll( '.brandy-tabs__tab' );
	const panels = container.querySelectorAll( '.brandy-tabs__panel' );

	tabs.forEach( ( tab, i ) => {
		const isActive = i === activeIndex;
		tab.classList.toggle( 'is-active', isActive );
		tab.setAttribute( 'aria-selected', String( isActive ) );
		tab.setAttribute( 'tabindex', isActive ? '0' : '-1' );
	} );

	panels.forEach( ( panel, i ) => {
		const isActive = i === activeIndex;
		panel.classList.toggle( 'is-active', isActive );
		if ( isActive ) {
			panel.removeAttribute( 'hidden' );
		} else {
			panel.setAttribute( 'hidden', '' );
		}
	} );

	if ( shouldFocus && tabs[ activeIndex ] ) {
		tabs[ activeIndex ].focus();
	}
};

store( 'brandy/tabs', {
	actions: {
		selectTab( event ) {
			const ctx = getContext();
			const { ref } = getElement();
			const container = ref.closest( '.brandy-tabs' );
			if ( ! container ) {
				return;
			}

			const button = event.target.closest( '[data-tab-index]' );
			if ( ! button ) {
				return;
			}

			const index = parseInt( button.dataset.tabIndex, 10 );
			if ( isNaN( index ) || index === ctx.activeTab ) {
				return;
			}

			ctx.activeTab = index;
			updateTabsState( container, index, false );
		},
		handleKeydown( event ) {
			const ctx = getContext();
			const { ref } = getElement();
			const container = ref.closest( '.brandy-tabs' );
			if ( ! container ) {
				return;
			}
			const tabs = container.querySelectorAll( '.brandy-tabs__tab' );
			const count = tabs.length;
			if ( ! count ) {
				return;
			}

			let newIndex = ctx.activeTab;

			switch ( event.key ) {
				case 'ArrowRight':
				case 'ArrowDown':
					newIndex = ( ctx.activeTab + 1 ) % count;
					break;
				case 'ArrowLeft':
				case 'ArrowUp':
					newIndex =
						( ctx.activeTab - 1 + count ) % count;
					break;
				case 'Home':
					newIndex = 0;
					break;
				case 'End':
					newIndex = count - 1;
					break;
				default:
					return;
			}

			event.preventDefault();
			ctx.activeTab = newIndex;
			updateTabsState( container, newIndex, true );
		},
	},
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();
			updateTabsState( ref, ctx.activeTab, false );
		},
	},
} );
